/**
 * Voice Log Button Component (Floating Action Button)
 *
 * Features:
 * - Hold-to-record functionality with Web Audio API
 * - Visual feedback during recording
 * - Draft verification UI
 * - Ready for OpenAI integration
 * - Intensity displayed as 1-5 stars
 *
 * INTEGRATION STATUS: Web Audio API fully implemented
 * TODO: Connect to Supabase Edge Function (see VOICE_LOGGER_SETUP.md)
 */

import { useState, useCallback } from 'react';
import { VoiceLogVerification } from './VoiceLogVerification';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import './VoiceLogButton.css';

interface VoiceLogButtonProps {
  onSetLogged: (exercise: string, reps?: number, weight?: number, intensity?: number) => void | Promise<void>;
}

interface ParsedSet {
  exercise: string;
  reps: number;
  weight?: number;
  intensity?: number;
}

export function VoiceLogButton({ onSetLogged }: VoiceLogButtonProps): JSX.Element {
  const [showVerification, setShowVerification] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedSet | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { isRecording, startRecording, stopRecording } = useVoiceRecorder();

  const handleMouseDown = useCallback((): void => {
    startRecording();
  }, [startRecording]);

  const handleMouseUp = useCallback(async (): Promise<void> => {
    const blob = await stopRecording();

    if (!blob) {
      console.warn('No audio data captured');
      return;
    }

    setIsProcessing(true);

    // TODO: Send to Supabase Edge Function
    // const formData = new FormData();
    // formData.append('audio', blob, 'workout-log.webm');
    //
    // const response = await fetch('/functions/v1/process-voice-log', {
    //   method: 'POST',
    //   headers: {
    //     'Authorization': `Bearer ${supabaseAnonKey}`,
    //   },
    //   body: formData,
    // });
    //
    // const result = await response.json();
    // setParsedData(result.data.parsed_sets[0]);

    // MOCK: Simulate AI parsing response
    console.log('🎤 Processing audio blob:', blob.size, 'bytes');
    setTimeout(() => {
      setParsedData({
        exercise: 'Bench Press',
        reps: 8,
        weight: 225,
        intensity: 4,
      });
      setShowVerification(true);
      setIsProcessing(false);
    }, 1500);
  }, [stopRecording]);

  const handleConfirm = (): void => {
    if (parsedData) {
      onSetLogged(parsedData.exercise, parsedData.reps, parsedData.weight, parsedData.intensity);
      setShowVerification(false);
      setParsedData(null);
    }
  };

  const handleEdit = (edited: ParsedSet): void => {
    onSetLogged(edited.exercise, edited.reps, edited.weight, edited.intensity);
    setShowVerification(false);
    setParsedData(null);
  };

  const handleCancel = (): void => {
    setShowVerification(false);
    setParsedData(null);
  };

  return (
    <>
      <button
        type="button"
        className={`voice-log-fab ${isRecording ? 'voice-log-fab--recording' : ''} ${
          isProcessing ? 'voice-log-fab--processing' : ''
        }`}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
        disabled={isProcessing}
        aria-label="Hold to record workout set"
      >
        {isProcessing ? (
          <svg
            className="voice-log-fab__icon voice-log-fab__icon--spinner"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
            <path
              d="M12 2a10 10 0 0 1 10 10"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg
            className="voice-log-fab__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        )}
        {isRecording && (
          <span className="voice-log-fab__pulse" />
        )}
      </button>

      {showVerification && parsedData && (
        <VoiceLogVerification
          data={parsedData}
          onConfirm={handleConfirm}
          onEdit={handleEdit}
          onCancel={handleCancel}
        />
      )}
    </>
  );
}
